// Articles of the "Guide de l'entrepreneur". Each article is a list of
// blocks: paragraph (p), sub-title (h2), bullet list (list) or table.
// `cta.from` starts the matching order; `cta.path` opens a page.

export const GUIDE_UPDATED = "2026-10-16";

export const guideArticles = [
  {
    slug: "creer-une-entreprise-au-cameroun",
    title: "Créer une entreprise au Cameroun : le guide complet",
    seoTitle: "Créer une entreprise au Cameroun : étapes, coûts et délais",
    description:
      "Étapes, documents, coûts et délais pour créer votre entreprise au Cameroun, en ligne et sans vous déplacer, avec Platform98.",
    summary:
      "Les étapes, les documents à réunir, ce que cela coûte et combien de temps il faut compter.",
    cta: { label: "Créer mon entreprise en ligne", from: "creation" },
    blocks: [
      {
        type: "p",
        text: "Créer son entreprise au Cameroun est devenu plus simple, mais la démarche demande toujours de bien préparer son dossier. Voici les étapes, les documents à réunir, ce que cela coûte et combien de temps il faut compter.",
      },
      { type: "h2", text: "1. Choisir la forme de votre entreprise" },
      {
        type: "p",
        text: "C'est la première décision, car elle fixe vos obligations, votre responsabilité et les documents à fournir. Les formes les plus courantes sont :",
      },
      {
        type: "list",
        items: [
          "L'Établissement (ETS) : vous exercez en votre nom propre. C'est la forme la plus simple et la moins chère.",
          "La SARLU : une société à responsabilité limitée avec un seul associé.",
          "La SARL : une société à responsabilité limitée avec plusieurs associés.",
          "L'association, pour une activité à but non lucratif.",
        ],
      },
      {
        type: "p",
        text: "Notre article « SARL, SARLU ou Établissement : quelle forme choisir ? » vous aide à décider.",
        link: { slug: "sarl-sarlu-ou-etablissement" },
      },
      { type: "h2", text: "2. Trouver et vérifier le nom" },
      {
        type: "p",
        text: "Le nom de votre entreprise doit être disponible. Si vous n'avez pas encore d'idée, Platform98 peut vous proposer un nom (10 000 FCFA). Pour éviter qu'une autre entreprise utilise le même, vous pouvez ensuite le protéger à l'OAPI.",
      },
      { type: "h2", text: "3. Choisir l'adresse du siège" },
      {
        type: "p",
        text: "Votre entreprise doit avoir une adresse. Vous pouvez utiliser vos propres locaux, ou une domiciliation : une adresse professionnelle mise à votre disposition, à partir de 22 000 FCFA par mois avec l'abonnement annuel. La domiciliation est idéale si vous travaillez depuis chez vous ou depuis l'étranger.",
      },
      { type: "h2", text: "4. Réunir les documents" },
      {
        type: "p",
        text: "Avec Platform98, les documents dépendent de la forme choisie :",
      },
      {
        type: "table",
        head: ["Forme", "Documents demandés"],
        rows: [
          [
            "Établissement (ETS)",
            "Pièce d'identité du gérant, plan de localisation",
          ],
          ["SARLU", "Pièce d'identité du gérant, plan de localisation"],
          [
            "SARL",
            "Pièce d'identité du gérant, pièces d'identité des associés, liste des associés avec la répartition des parts, plan de localisation",
          ],
          [
            "Association",
            "Pièce d'identité du président, statuts datés et signés, règlement intérieur, procès-verbal de l'assemblée générale constitutive, plan de localisation",
          ],
        ],
      },
      {
        type: "p",
        text: "La pièce d'identité peut être un passeport ou, pour les étrangers, une carte de séjour. Le plan de localisation doit porter votre numéro de téléphone et votre signature.",
      },
      { type: "h2", text: "5. Déposer votre demande en ligne" },
      {
        type: "p",
        text: "Avec Platform98, vous n'avez pas à vous déplacer : vous remplissez le formulaire en ligne, vous envoyez vos documents, vous payez par Mobile Money ou par carte bancaire, et nos conseillers s'occupent de toutes les démarches auprès des administrations. Vous suivez l'avancement depuis votre espace et récupérez vos documents officiels en ligne.",
      },
      { type: "h2", text: "Combien ça coûte ?" },
      {
        type: "p",
        text: "Les tarifs Platform98 pour la création de votre entreprise :",
      },
      {
        type: "table",
        head: ["Forme", "Procédure classique", "Procédure express"],
        rows: [
          ["Établissement (ETS)", "79 000 FCFA", "95 000 FCFA"],
          ["SARLU", "185 000 FCFA", "200 000 FCFA"],
          ["SARL", "215 000 FCFA", "230 000 FCFA"],
          ["Association", "100 000 FCFA", "Non disponible"],
        ],
      },
      { type: "h2", text: "Combien de temps ?" },
      {
        type: "list",
        items: [
          "Procédure classique : environ 1 semaine.",
          "Procédure express : environ 3 jours.",
          "Association : environ 1 mois.",
        ],
      },
      {
        type: "p",
        text: "Les délais commencent une fois votre dossier complet et payé.",
      },
      { type: "h2", text: "Et après la création ?" },
      {
        type: "p",
        text: "Pensez aux démarches suivantes : attestation de conformité fiscale, immatriculation de l'entreprise à la CNPS, et protection de votre nom ou de votre marque à l'OAPI. Vous pouvez tout commander depuis votre espace Platform98.",
      },
    ],
  },
  {
    slug: "sarl-sarlu-ou-etablissement",
    title: "SARL, SARLU ou Établissement : comment choisir ?",
    seoTitle: "SARL, SARLU ou Établissement : quelle forme choisir ?",
    description:
      "Établissement, SARLU ou SARL au Cameroun : responsabilité, nombre d'associés, coûts et documents, pour choisir la forme adaptée à votre projet.",
    summary:
      "Les différences essentielles pour choisir la forme adaptée à votre projet.",
    cta: { label: "Créer mon entreprise", from: "creation" },
    blocks: [
      {
        type: "p",
        text: "La forme de votre entreprise a des conséquences sur votre patrimoine, sur la façon de vous associer et sur vos coûts. Voici les différences essentielles.",
      },
      { type: "h2", text: "L'Établissement (ETS) : la simplicité" },
      {
        type: "p",
        text: "Vous exercez en votre nom propre, sans créer de société distincte.",
      },
      {
        type: "list",
        items: [
          "Pour qui : un commerçant ou un prestataire qui se lance seul, avec peu de risques financiers.",
          "Avantages : la création la plus rapide et la moins chère (79 000 FCFA avec Platform98), et peu de documents (pièce d'identité et plan de localisation).",
          "Limites : vos biens personnels et ceux de l'activité ne sont pas séparés. En cas de dettes, vos biens personnels peuvent être engagés.",
        ],
      },
      { type: "h2", text: "La SARLU : seul, mais protégé" },
      {
        type: "p",
        text: "C'est une société à responsabilité limitée avec un seul associé.",
      },
      {
        type: "list",
        items: [
          "Pour qui : un entrepreneur seul qui veut protéger ses biens personnels ou faire grandir son activité.",
          "Avantages : votre responsabilité est en principe limitée à vos apports. La société a sa propre existence, ce qui rassure les banques et les clients.",
          "Capital social minimum : 100 000 FCFA.",
          "Coût avec Platform98 : 185 000 FCFA (200 000 FCFA en express).",
        ],
      },
      { type: "h2", text: "La SARL : à plusieurs" },
      { type: "p", text: "C'est la forme la plus répandue pour s'associer." },
      {
        type: "list",
        items: [
          "Pour qui : plusieurs associés qui veulent créer ensemble.",
          "Avantages : la responsabilité de chaque associé est en principe limitée à ses apports, et la répartition des parts organise le pouvoir de chacun.",
          "À prévoir : les pièces d'identité de tous les associés et la liste des associés avec la répartition des parts.",
          "Capital social minimum : 100 000 FCFA.",
          "Coût avec Platform98 : 215 000 FCFA (230 000 FCFA en express).",
        ],
      },
      { type: "h2", text: "En résumé" },
      {
        type: "table",
        head: ["", "Établissement", "SARLU", "SARL"],
        rows: [
          ["Nombre de personnes", "1", "1", "2 ou plus"],
          [
            "Patrimoine personnel protégé",
            "Non",
            "En principe oui",
            "En principe oui",
          ],
          ["Capital social minimum", "Aucun", "100 000 FCFA", "100 000 FCFA"],
          [
            "Prix Platform98 (classique)",
            "79 000 FCFA",
            "185 000 FCFA",
            "215 000 FCFA",
          ],
          [
            "Délai (classique / express)",
            "1 semaine / 3 jours",
            "1 semaine / 3 jours",
            "1 semaine / 3 jours",
          ],
        ],
      },
      { type: "h2", text: "Notre conseil" },
      {
        type: "p",
        text: "Si vous démarrez seul une petite activité, l'Établissement suffit souvent. Si vous prévoyez d'investir, d'embaucher ou de travailler avec de grandes entreprises, une SARLU ou une SARL est généralement plus adaptée. En cas de doute, nos conseillers vous orientent avant la création.",
      },
    ],
  },
  {
    slug: "creer-son-entreprise-depuis-l-etranger",
    title: "Diaspora : créer votre entreprise au Cameroun sans vous déplacer",
    seoTitle: "Créer son entreprise au Cameroun depuis l'étranger",
    description:
      "Vous vivez à l'étranger ? Créez votre entreprise au Cameroun en ligne : documents, adresse du siège, paiement par carte et suivi à distance.",
    summary:
      "Toute la création en ligne, de l'envoi des documents au paiement par carte.",
    cta: { label: "Créer mon entreprise depuis l'étranger", from: "creation" },
    blocks: [
      {
        type: "p",
        text: "Vous vivez en France, au Canada, aux États-Unis ou ailleurs, et vous voulez lancer une activité au Cameroun ? Avec Platform98, toute la création se fait en ligne.",
      },
      { type: "h2", text: "Ce dont vous avez besoin" },
      {
        type: "list",
        items: [
          "Une pièce d'identité : votre passeport, ou une carte de séjour si vous êtes étranger.",
          "Une adresse au Cameroun pour le siège. Si vous n'avez pas de locaux sur place, la domiciliation Platform98 vous fournit une adresse professionnelle, à partir de 22 000 FCFA par mois avec l'abonnement annuel.",
          "Les documents propres à la forme choisie, par exemple la liste des associés pour une SARL.",
        ],
      },
      { type: "h2", text: "Vous êtes de nationalité étrangère ?" },
      {
        type: "list",
        items: [
          "Si vous êtes associé majoritaire, un agrément est nécessaire pour exercer une activité commerciale.",
          "Si vous n'êtes pas majoritaire, votre titre de séjour suffit.",
        ],
      },
      { type: "h2", text: "Comment se passe la démarche ?" },
      {
        type: "list",
        ordered: true,
        items: [
          "Vous créez votre compte sur Platform98. Un code de vérification vous est envoyé par e-mail et par SMS.",
          "Vous choisissez la forme de l'entreprise, la procédure (classique ou express) et l'adresse du siège.",
          "Vous envoyez vos documents depuis votre ordinateur ou votre téléphone.",
          "Vous payez en ligne, par carte bancaire (Visa, Mastercard) ou par Mobile Money.",
          "Nos conseillers s'occupent des démarches et vous suivez chaque étape depuis votre espace.",
          "Vous récupérez vos documents officiels en ligne.",
        ],
      },
      { type: "h2", text: "Le paiement par carte depuis l'étranger" },
      {
        type: "p",
        text: "Le paiement par carte se fait sur la page sécurisée de notre partenaire de paiement. Le montant est converti au cours du jour, et des frais de paiement par carte peuvent s'ajouter.",
      },
      { type: "h2", text: "Délais" },
      {
        type: "p",
        text: "Comptez environ 3 jours en procédure express et une semaine en procédure classique, une fois le dossier complet et payé.",
      },
      { type: "h2", text: "Gérer votre entreprise à distance" },
      {
        type: "p",
        text: "Après la création, Platform98 vous accompagne aussi pour la suite : gestion administrative, suivi juridique, logiciel de gestion (ERP) et formalités comme le NIU ou la conformité fiscale.",
      },
    ],
  },
  {
    slug: "niu-cameroun",
    title: "Le NIU au Cameroun : à quoi il sert et comment l'obtenir",
    seoTitle: "NIU au Cameroun : à quoi il sert et comment l'obtenir",
    description:
      "Le Numéro d'Identifiant Unique (NIU) : à quoi il sert, qui doit l'avoir et comment obtenir votre attestation d'immatriculation en ligne.",
    summary:
      "L'identifiant fiscal, l'attestation d'immatriculation et l'attestation de conformité fiscale.",
    cta: { label: "Demander mon attestation", path: "/formalities" },
    blocks: [
      {
        type: "p",
        text: "Le NIU (Numéro d'Identifiant Unique) est l'identifiant fiscal attribué par l'administration fiscale camerounaise. Il identifie une personne ou une entreprise dans toutes ses relations avec les impôts.",
      },
      { type: "h2", text: "À quoi sert le NIU ?" },
      {
        type: "p",
        text: "Il vous est demandé dans de nombreuses situations : déclarer et payer vos impôts, facturer des entreprises, répondre à des marchés ou obtenir d'autres attestations, comme l'attestation de conformité fiscale.",
      },
      { type: "h2", text: "Qui doit avoir un NIU ?" },
      {
        type: "list",
        items: [
          "Les entreprises : il leur est attribué lors de la création.",
          "Les personnes physiques qui exercent une activité ou qui en ont besoin pour leurs démarches.",
        ],
      },
      { type: "h2", text: "L'attestation d'immatriculation" },
      {
        type: "p",
        text: "L'attestation d'immatriculation est le document qui prouve votre NIU. Elle vous est souvent demandée par vos partenaires, vos clients ou votre banque.",
      },
      {
        type: "p",
        text: "Avec Platform98, vous pouvez la demander en ligne pour 5 000 FCFA, que vous soyez une personne ou une entreprise, et même si vous êtes déjà immatriculé et avez seulement besoin d'une nouvelle attestation.",
      },
      { type: "h2", text: "Et l'attestation de conformité fiscale ?" },
      {
        type: "p",
        text: "C'est un autre document : il atteste que votre situation fiscale est à jour. Il est souvent exigé pour les marchés publics et par certains partenaires, et doit être renouvelé régulièrement. Platform98 vous l'obtient aussi pour 5 000 FCFA, et vous pouvez le recommander chaque mois si besoin.",
      },
    ],
  },
  {
    slug: "proteger-nom-et-marque-oapi",
    title: "Protéger le nom et la marque de votre entreprise à l'OAPI",
    seoTitle: "Protéger le nom et la marque de son entreprise à l'OAPI",
    description:
      "Nom commercial ou marque : comment les protéger à l'OAPI, combien de temps cela prend et ce que cela coûte, avec Platform98.",
    summary:
      "Nom commercial ou marque : les étapes, les délais et les prix de la protection.",
    cta: { label: "Protéger mon nom ou ma marque", path: "/formalities" },
    blocks: [
      {
        type: "p",
        text: "Vous avez trouvé un nom pour votre entreprise, un logo pour vos produits ? Sans protection, rien n'empêche un concurrent de les utiliser. Au Cameroun, cette protection se fait auprès de l'OAPI (Organisation Africaine de la Propriété Intellectuelle), dont le siège est à Yaoundé. Une seule demande vous protège dans les 17 pays membres de l'OAPI.",
      },
      { type: "h2", text: "Nom commercial ou marque : quelle différence ?" },
      {
        type: "list",
        items: [
          "Le nom commercial est le nom sous lequel votre entreprise est connue de ses clients.",
          "La marque est un signe (nom, logo) qui distingue vos produits ou services. Elle est déposée pour une ou plusieurs classes, c'est-à-dire des catégories de produits ou de services.",
        ],
      },
      { type: "p", text: "Beaucoup d'entreprises protègent les deux." },
      { type: "h2", text: "Protéger le nom commercial" },
      { type: "p", text: "La démarche se fait en deux temps :" },
      {
        type: "list",
        ordered: true,
        items: [
          "Une recherche d'antériorité d'environ deux semaines, pour vérifier que le nom n'est pas déjà protégé.",
          "Le dépôt. Votre nom est protégé dès le dépôt ; la délivrance des documents officiels par l'OAPI prend ensuite 3 à 6 mois.",
        ],
      },
      {
        type: "p",
        text: "Avec Platform98 : 215 000 FCFA. Les documents demandés sont le registre de commerce et la pièce d'identité du représentant.",
      },
      { type: "h2", text: "Protéger la marque" },
      {
        type: "p",
        text: "Vous fournissez la pièce d'identité du déposant (ou les documents de l'entreprise) et le logo de votre marque. Les délais sont les mêmes que pour le nom commercial : environ deux semaines de recherche, puis 3 à 6 mois pour la délivrance des documents officiels par l'OAPI. Avec Platform98 : 750 000 FCFA pour une classe.",
      },
      { type: "h2", text: "Combien de temps dure la protection ?" },
      {
        type: "p",
        text: "La protection d'une marque comme celle d'un nom commercial dure 10 ans, et elle peut être renouvelée.",
      },
      { type: "h2", text: "Pourquoi le faire tôt ?" },
      {
        type: "p",
        text: "C'est le premier déposant qui est protégé. Plus vous attendez, plus le risque qu'un autre dépose votre nom ou votre marque avant vous augmente. Si vous créez votre entreprise avec Platform98, vous pouvez ajouter la protection du nom commercial dès la création.",
      },
    ],
  },
];

export const findArticle = (slug) =>
  guideArticles.find((article) => article.slug === slug);
